import { z } from 'zod'

import {
  categories as ccmCategories,
  supportedTools,
} from '@cyborg-monorepo/hunter/matchboi'

import { Enums } from './enums'
import { deepStrict } from './utils'

const ContextSchema = z.object({
  actors: z.array(z.string()),
  operations: z.array(z.string()),
  threat_names: z.array(z.string()),
})

const MitreSchema = z.object({
  mitre_attack_technique_ids: z.array(z.string()),
  mitre_attack_techniques: z.array(z.string()),
  mitre_attack_tactics: z.array(z.string()),
})

const MappingSchema = z.object({
  mitre: MitreSchema,
  exploit_or_vulns: z.array(z.string()),
})

const CollectionRecordSchema = z.object({
  UUID: z.string().uuid(),
  title: z.string(),
  status: z.string(),
  created_date: z.string().datetime(),
  last_updated: z.string().datetime({ offset: true }),
  completed_date: z.string().datetime(),
  threat_summary: z.string(),
  synopsis: z.string().max(5000),
  references: z.array(z.string()),
  context: ContextSchema,
  mapping: MappingSchema,
  hunter_packages: z.array(z.string()),
  threat_profiles: z.array(z.string()),
  collection_type: z.nativeEnum(Enums.CollectionTypeEnum),
  emerging_threat: z.boolean(),
})

// Used for gathering warnings
// const CollectionStrictSchema = CollectionRecordSchema.strict();
const CollectionStrictSchema = deepStrict(
  CollectionRecordSchema,
  'Deep strict error',
)

type CollectionSchemaType = z.infer<typeof CollectionRecordSchema>

export { CollectionRecordSchema, CollectionSchemaType, CollectionStrictSchema }

const ThreatProfileContextSchema = z.object({
  actors: z.array(z.string()),
  motivations: z.array(z.string()),
  target_regions: z.array(z.string()),
  target_countries: z.array(z.string()),
  target_industries: z.array(z.string()),
  source_regions: z.array(z.string()),
  source_countries: z.array(z.string()),
  target_oses: z.array(z.string()),
  attack_surfaces: z.array(z.string()),
  threat_categories: z.array(z.string()),
  threat_types: z.array(z.string()),
  threat_name: z.string(),
})

const ThreatProfileMappingSchema = z.object({
  mitre: z.object({
    mitre_techniques_overall: z.array(z.string()),
    mitre_software_id: z.string().optional(),
  }),
})

const ThreatProfileSchema = z.object({
  UUID: z.string(),
  profile_id: z.string(),
  profile_name: z.string(),
  title: z.string(),
  profile_type: z.string(),
  also_known_as: z.array(z.string()),
  overview: z.string(),
  targeting: z.string().optional(),
  delivery: z.string().optional(),
  installation: z.string().optional(),
  persistence: z.string().optional(),
  modules: z.string().optional(),
  communication: z.string().optional(),
  references: z.array(
    z.string().refine(
      (ref) => {
        const lower = ref.toLowerCase()
        return (
          !lower.includes('titan.intel471.com') &&
          !lower.includes('verity.intel471.com')
        )
      },
      {
        message:
          'TITAN and Verity URLs belong in intel471_references, not references',
      },
    ),
  ),
  intel471_references: z.array(z.string()).optional(),
  last_updated: z.string().datetime({ precision: 6, offset: true }),
  created_date: z.string().datetime(),
  impact: z.array(z.string()),
  context: ThreatProfileContextSchema,
  mapping: ThreatProfileMappingSchema,
})

// Used for gathering warnings
// const ThreatProfileStrictSchema = ThreatProfileSchema.strict();
const ThreatProfileStrictSchema = deepStrict(
  ThreatProfileSchema,
  'Deep strict error',
)

type ThreatProfileSchemaType = z.infer<typeof ThreatProfileSchema>
export {
  ThreatProfileSchema,
  ThreatProfileSchemaType,
  ThreatProfileStrictSchema,
}

const ChangelogSchema = z.object({
  date: z.string(),
  note: z.string(),
})

const DeploymentRequirementsSchema = z
  .object({
    categories: z.array(z.string()),
    searchable_data: z.array(z.string()),
    deployment_steps: z.string(),
  })
  .superRefine((data, ctx) => {
    const validFields = new Set<string>()
    for (const cat of data.categories) {
      if (!(cat in ccmCategories)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `"${cat}" is not a recognized CCM category`,
          path: ['categories'],
        })
      } else {
        ccmCategories[cat].ccm_fields.forEach((f) => validFields.add(f))
      }
    }
    for (const field of data.searchable_data) {
      if (!validFields.has(field)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `searchable_data field "${field}" is not valid for the declared categories`,
          path: ['searchable_data'],
        })
      }
    }
  })

// Define the ContentSchema separately
const ContentSchema = z.object({
  uuids: z.array(z.string().uuid()),
  tools: z.array(z.string()),
  categories: z.array(z.string()),
  records: z.array(z.any()), // Separate Schema for content validation
})

const ContentRecordSchema = z.object({
  UUID: z.string().uuid(),
  author: z.string().optional(),
  title: z.string(),
  date_created: z.string(z.date()),
  last_updated: z.string(z.date()),
  status: z.string(),
  content_ingestion: z.string(),
  platform_type: z.string(),
  tool: z.enum(supportedTools),
  goal: z.nativeEnum(Enums.Goals),
  source: z.string(),
  deployment_requirements: DeploymentRequirementsSchema,
  content: z.string(),
  additional_queries: z.array(z.string()).optional(),
  change_log: z.array(ChangelogSchema),
})

// Used for gathering warnings
// const ContentRecordStrictSchema = ContentRecordSchema.strict();
const ContentRecordStrictSchema = deepStrict(
  ContentRecordSchema,
  'Deep strict error',
)

type ContentRecordSchemaType = z.infer<typeof ContentRecordSchema>

const HuntPackageBaseSchema = z.object({
  title: z.string(),
  UUID: z.string().uuid(),
  status: z.string(),
  community: z.boolean(),
  author: z.string(),
  created_date: z.string().datetime(),
  last_updated: z.string().datetime({ offset: true }),
  completed_date: z.string().datetime(),
  source: z.string(),
  sigma: z.string(),
  source_origin: z.string(),
  severity: z.string(),
  query_logic: z.object({
    table: z.array(
      z.object({
        selection: z.string(),
        field: z.string(),
        value: z.array(z.string()),
        grouped: z.boolean(),
      }),
    ),
    condition: z.string(),
  }),
  dependencies: z.array(z.string()),
  description: z.string().max(5000),
  hunt_category: z.nativeEnum(Enums.HuntCategoryEnum),
  references: z.object({
    general: z.array(z.string()),
    analysis: z.array(z.string()),
    deep_dives: z.array(z.string()),
    blog_links: z.array(z.string()),
    malware_samples: z.array(z.string()),
  }),
  complexity: z.object({
    research: z.number().int().min(1).max(5),
    development: z.number().int().min(1).max(5),
    validation: z.number().int().min(1).max(5),
    enrichment: z.number().int().min(1).max(5),
    score: z.number().int().min(1).max(100),
    loe: z.string(),
    hours: z.number().int().min(1).max(18),
  }),
  response_actions: z.object({
    analyst_runbook: z.string(),
    mitigation_recommendations: z.string(),
  }),
  running_analyst_notes: z.array(
    z.object({
      analyst_note_date: z.string(z.date()),
      analyst_note_type: z.nativeEnum(Enums.AnalystNoteTypeEnum),
      analyst_note_author: z.nativeEnum(Enums.AnalystNoteAuthorEnum),
      current_analyst_note: z.string(),
      analyst_note_summary: z.string(),
      analyst_note_pivots: z.string(),
      analyst_note_attribution: z.string(),
      huntpackage_attribution: z.array(z.string().uuid()),
      malware_names: z.array(z.string()),
      adversary_names: z.array(z.string()),
      observed_commandlines: z.array(z.string()),
      references: z.object({
        titan_references: z.array(
          z.object({ title: z.string(), link: z.string() }),
        ),
        cyborg_sources: z.array(
          z.object({ title: z.string(), link: z.string() }),
        ),
        general_references: z.array(
          z.object({ title: z.string(), link: z.string() }),
        ),
      }),
    }),
  ),
  validation: z.string(),
  validation_types: z.array(z.string()),
  validation_encoded_zip: z.string().optional(),
  change_log: z.array(ChangelogSchema),
  content: ContentSchema,
  context: z.object({
    tooling: z.array(z.string()).optional(),
    threat_names: z.array(z.string()).optional(),
    threat_description: z.string(),
    threat_categories: z.array(z.string()),
    actors: z.array(z.string()).optional(),
    target_industries: z.array(z.string()).optional(),
    target_countries: z.array(z.string()).optional(),
    target_regions: z.array(z.string()).optional(),
    source_countries: z.array(z.string()).optional(),
    source_regions: z.array(z.string()).optional(),
    operations: z.array(z.string()).optional(),
    target_oses: z.array(z.string()),
    target_os_versions: z.array(z.string()).optional(),
    campaigns: z.array(z.string()).optional(),
    attack_surfaces: z.array(z.string()),
    motivations: z.array(z.string()).optional(),
  }),
  mapping: z.object({
    mitre: z.object({
      mitre_attack_payload: z.array(
        z.record(
          z.object({
            technique_name: z.string(),
            tactics: z.array(z.string()),
          }),
        ),
      ),
      mitre_attack_tactics: z.array(z.string()),
      mitre_attack_techniques: z.array(z.string()),
      mitre_attack_technique_ids: z.array(z.string()),
    }),
    kill_chains: z.array(z.string()),
    diamond_model: z.string(),
    exploit_or_vulns: z.array(z.string()).optional(),
    cwe_ids: z.array(z.string()).optional(),
  }),
})

// Full schema for HuntPackage, including content
const HuntPackageSchema = HuntPackageBaseSchema.extend({
  content: ContentSchema.extend({
    uuids: z.array(z.string().uuid()),
    tools: z.array(z.string()),
    categories: z.array(z.string()),
    records: z.array(ContentRecordSchema), // Extend the records field
  }),
})

// Used for gathering warnings
const HuntPackageBaseStrictSchema = deepStrict(
  HuntPackageBaseSchema,
  'Deep strict error',
)

type HuntPackageSchemaType = z.infer<typeof HuntPackageSchema>
export {
  HuntPackageSchema,
  HuntPackageSchemaType,
  HuntPackageBaseSchema,
  HuntPackageBaseStrictSchema,
  ContentRecordSchema,
  ContentRecordStrictSchema,
  ContentRecordSchemaType,
}
